import _defineProperty from "/near-peer-app/nearpeer-frontend/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { getAnimationData, getMotionValue } from './data.es.js';
import { isCssVar, registerCssVariable } from './utils/css-var.es.js';
import { defaults, isEasingGenerator, isFunction, isEasingList, isNumber, time, noop } from '@motionone/utils';
import { isTransform, addTransformToElement, transformDefinitions } from './utils/transforms.es.js';
import { convertEasing } from './utils/easing.es.js';
import { supports } from './utils/feature-detection.es.js';
import { hydrateKeyframes, keyframesList } from './utils/keyframes.es.js';
import { style } from './style.es.js';
import { getStyleName } from './utils/get-style-name.es.js';
import { stopAnimation } from './utils/stop-animation.es.js';
import { getUnitConverter } from './utils/get-unit.es.js';
function getDevToolsRecord() {
  return window.__MOTION_DEV_TOOLS_RECORD;
}
function animateStyle(element, key, keyframesDefinition) {
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var AnimationPolyfill = arguments.length > 4 ? arguments[4] : undefined;
  var record = getDevToolsRecord();
  var isRecording = options.record !== false && record;
  var animation;
  var _options$duration = options.duration,
    duration = _options$duration === void 0 ? defaults.duration : _options$duration,
    _options$delay = options.delay,
    delay = _options$delay === void 0 ? defaults.delay : _options$delay,
    _options$endDelay = options.endDelay,
    endDelay = _options$endDelay === void 0 ? defaults.endDelay : _options$endDelay,
    _options$repeat = options.repeat,
    repeat = _options$repeat === void 0 ? defaults.repeat : _options$repeat,
    _options$easing = options.easing,
    easing = _options$easing === void 0 ? defaults.easing : _options$easing,
    _options$persist = options.persist,
    persist = _options$persist === void 0 ? false : _options$persist,
    direction = options.direction,
    offset = options.offset,
    _options$allowWebkitA = options.allowWebkitAcceleration,
    allowWebkitAcceleration = _options$allowWebkitA === void 0 ? false : _options$allowWebkitA,
    _options$autoplay = options.autoplay,
    autoplay = _options$autoplay === void 0 ? true : _options$autoplay;
  var data = getAnimationData(element);
  var valueIsTransform = isTransform(key);
  var canAnimateNatively = supports.waapi();
  /**
   * If this is an individual transform, we need to map its
   * key to a CSS variable and update the element's transform style
   */
  valueIsTransform && addTransformToElement(element, key);
  var name = getStyleName(key);
  var motionValue = getMotionValue(data.values, name);
  /**
   * Get definition of value, this will be used to convert numerical
   * keyframes into the default value type.
   */
  var definition = transformDefinitions.get(name);
  /**
   * Stop the current animation, if any. Because this will trigger
   * commitStyles (DOM writes) and we might later trigger DOM reads,
   * this is fired now and we return a factory function to create
   * the actual animation that can get called in batch,
   */
  stopAnimation(motionValue.animation, !(isEasingGenerator(easing) && motionValue.generator) && options.record !== false);
  /**
   * Batchable factory function containing all DOM reads.
   */
  return function () {
    var readInitialValue = function readInitialValue() {
      var _a, _b;
      return (_b = (_a = style.get(element, name)) !== null && _a !== void 0 ? _a : definition === null || definition === void 0 ? void 0 : definition.initialValue) !== null && _b !== void 0 ? _b : 0;
    };
    /**
     * Replace null values with the previous keyframe value, or read
     * it from the DOM if it's the first keyframe.
     */
    var keyframes = hydrateKeyframes(keyframesList(keyframesDefinition), readInitialValue);
    /**
     * Detect unit type of keyframes.
     */
    var toUnit = getUnitConverter(keyframes, definition);
    if (isEasingGenerator(easing)) {
      var custom = easing.createAnimation(keyframes, key !== "opacity", readInitialValue, name, motionValue);
      easing = custom.easing;
      keyframes = custom.keyframes || keyframes;
      duration = custom.duration || duration;
    }
    /**
     * If this is a CSS variable we need to register it with the browser
     * before it can be animated natively. We also set it with setProperty
     * rather than directly onto the element.style object.
     */
    if (isCssVar(name)) {
      if (supports.cssRegisterProperty()) {
        registerCssVariable(name);
      } else {
        canAnimateNatively = false;
      }
    }
    /**
     * If we've been passed a custom easing function, and this browser
     * does **not** support linear() easing, and the value is a transform
     * (and thus a pure number) we can still support the custom easing
     * by falling back to the animation polyfill.
     */
    if (valueIsTransform && !supports.linearEasing() && (isFunction(easing) || isEasingList(easing) && easing.some(isFunction))) {
      canAnimateNatively = false;
    }
    /**
     * If we can animate this value with WAAPI, do so.
     */
    if (canAnimateNatively) {
      /**
       * Convert numbers to default value types. Currently this only supports
       * transforms but it could also support other value types.
       */
      if (definition) {
        keyframes = keyframes.map(function (value) {
          return isNumber(value) ? definition.toDefaultUnit(value) : value;
        });
      }
      /**
       * If this browser doesn't support partial/implicit keyframes we need to
       * explicitly provide one.
       */
      if (keyframes.length === 1 && (!supports.partialKeyframes() || isRecording)) {
        keyframes.unshift(readInitialValue());
      }
      var animationOptions = {
        delay: time.ms(delay),
        duration: time.ms(duration),
        endDelay: time.ms(endDelay),
        easing: !isEasingList(easing) ? convertEasing(easing, duration) : undefined,
        direction: direction,
        iterations: repeat + 1,
        fill: "both"
      };
      animation = element.animate(_defineProperty(_defineProperty(_defineProperty({}, name, keyframes), "offset", offset), "easing", isEasingList(easing) ? easing.map(function (thisEasing) {
        return convertEasing(thisEasing, duration);
      }) : undefined), animationOptions);
      /**
       * Polyfill finished Promise in browsers that don't support it
       */
      if (!animation.finished) {
        animation.finished = new Promise(function (resolve, reject) {
          animation.onfinish = resolve;
          animation.oncancel = reject;
        });
      }
      var target = keyframes[keyframes.length - 1];
      animation.finished.then(function () {
        if (persist) return;
        // Apply styles to target
        style.set(element, name, target);
        // Ensure fill modes don't persist
        animation.cancel();
      })["catch"](noop);
      /**
       * This forces Webkit to run animations on the main thread by exploiting
       * this condition:
       * https://trac.webkit.org/browser/webkit/trunk/Source/WebCore/platform/graphics/ca/GraphicsLayerCA.cpp?rev=281238#L1099
       *
       * This fixes Webkit's timing bugs, like accelerated animations falling
       * out of sync with main thread animations and massive delays in starting
       * accelerated animations in WKWebView.
       */
      if (!allowWebkitAcceleration) animation.playbackRate = 1.000001;
      /**
       * If we can't animate the value natively then we can fallback to the numbers-only
       * polyfill for transforms.
       */
    } else if (AnimationPolyfill && valueIsTransform) {
      /**
       * If any keyframe is a string (because we measured it from the DOM), we need to convert
       * it into a number before passing to the Animation polyfill.
       */
      keyframes = keyframes.map(function (value) {
        return typeof value === "string" ? parseFloat(value) : value;
      });
      /**
       * If we only have a single keyframe, we need to create an initial keyframe by reading
       * the current value from the DOM.
       */
      if (keyframes.length === 1) {
        keyframes.unshift(parseFloat(readInitialValue()));
      }
      animation = new AnimationPolyfill(function (latest) {
        style.set(element, name, toUnit ? toUnit(latest) : latest);
      }, keyframes, Object.assign(Object.assign({}, options), {
        duration: duration,
        easing: easing
      }));
    } else {
      var _target = keyframes[keyframes.length - 1];
      style.set(element, name, definition && isNumber(_target) ? definition.toDefaultUnit(_target) : _target);
    }
    if (isRecording) {
      record(element, key, keyframes, {
        duration: duration,
        delay: delay,
        easing: easing,
        repeat: repeat,
        offset: offset
      }, "motion-one");
    }
    motionValue.setAnimation(animation);
    if (animation && !autoplay) animation.pause();
    return animation;
  };
}
export { animateStyle };